import { ArrowUpRight } from "lucide-react";

const SERVICES = [
  {
    id: "general-contracting",
    title: "Residential General Contracting",
    blurb:
      "Single-point accountability for your build — from permits and trades to schedule and quality control. The Sativa team handles every detail.",
    image:
      "https://customer-assets.emergentagent.com/job_resi-contractor/artifacts/h43hmg2e_IMG_5645.JPG",
    feature: true,
  },
  {
    id: "custom-homes",
    title: "Custom Homes",
    blurb:
      "Architect-grade custom homes designed around how you live. Built with healthier materials and a build envelope made to last.",
    image:
      "https://customer-assets.emergentagent.com/job_resi-contractor/artifacts/fxs2go77_IMG_5647.JPG",
  },
  {
    id: "renovations",
    title: "Renovations",
    blurb:
      "Whole-home refreshes and structural renovations done with respect for what's already there — and what it should become.",
    image:
      "https://customer-assets.emergentagent.com/job_resi-contractor/artifacts/3kcx34nx_IMG_5649.JPG",
  },
  {
    id: "additions",
    title: "Additions",
    blurb:
      "Thoughtful additions that look like they were always part of the home — never bolted on. Engineered for the long haul.",
    image:
      "https://customer-assets.emergentagent.com/job_resi-contractor/artifacts/te7gz2i9_IMG_5648.JPG",
  },
  {
    id: "kitchens-baths",
    title: "Kitchens & Baths",
    blurb:
      "The two rooms that move a home — done at a finish-carpentry standard, with low-tox finishes and zero corners cut.",
    image:
      "https://images.unsplash.com/photo-1774716925788-5c4fc543d688?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjbGVhbiUyMG1pbmltYWxpc3QlMjBraXRjaGVuJTIwaW50ZXJpb3J8ZW58MHx8fHwxNzc5MjI3NDgxfDA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "adus",
    title: "ADUs (Accessory Dwelling Units)",
    blurb:
      "Backyard cottages, in-law suites, and detached studios. Compact, durable, and beautifully built — drawing on our Z Panel DNA.",
    image:
      "https://images.unsplash.com/photo-1720876470648-ba8b314b47ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBzdXN0YWluYWJsZSUyMGhvbWUlMjBleHRlcmlvciUyMGZvcmVzdHxlbnwwfHx8fDE3NzkyMjc0NjV8MA&ixlib=rb-4.1.0&q=85",
  },
];

export const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-[#F4F3EF] py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <div className="reveal grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-16 lg:mb-20 items-end">
          <div className="lg:col-span-5">
            <div className="text-xs tracking-[0.25em] uppercase font-bold text-[#9E8262]">
              01 — What We Build
            </div>
            <h2 className="font-display font-bold text-[#1A1D1A] mt-5 text-4xl sm:text-5xl lg:text-[56px] leading-[1.04] tracking-tight">
              Six ways we&apos;ll
              <br />
              <span className="italic font-light text-[#2C4230]">build for you.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="font-body text-[#4A524A] text-base sm:text-lg leading-relaxed">
              From a new kitchen to a whole new home, we bring the same standard
              to every project: clean job sites, transparent communication, and
              the kind of craftsmanship that makes inspectors raise an eyebrow —
              in the good way.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {SERVICES.map((s, i) => (
            <ServiceCard key={s.id} service={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

const ServiceCard = ({ service, index }) => {
  return (
    <article
      data-testid={`service-card-${service.id}`}
      className="reveal group relative bg-[#EBE9E2] border border-[#DCD9CE] overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-25px_rgba(26,29,26,0.25)]"
      style={{ transitionDelay: `${index * 60}ms` }}
    >
      <div className="relative aspect-[5/4] overflow-hidden bg-[#DCD9CE]">
        <img
          src={service.image}
          alt={service.title}
          className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1D1A]/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>

      <div className="p-7 sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <h3 className="font-display font-bold text-[#1A1D1A] text-[22px] sm:text-2xl leading-tight tracking-tight">
            {service.title}
          </h3>
          <ArrowUpRight
            size={20}
            className="text-[#2C4230] mt-1 shrink-0 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
          />
        </div>
        <p className="font-body text-[#4A524A] mt-4 text-[14.5px] leading-relaxed">
          {service.blurb}
        </p>
      </div>
    </article>
  );
};
